import { Controller } from "@hotwired/stimulus"

/**
 * Sortable HTML table — click a column header to sort rows.
 *
 * Attached to a <table> with:
 *   data-controller="sortable-table"
 *   <th data-sortable-table-column="name"> on each sortable column
 *
 * Sort behavior:
 *   - First click on a column sorts ascending.
 *   - Clicking the same column again toggles ascending/descending.
 *   - Values are compared numerically when both cells parse as finite
 *     numbers (supports currency/% via a tolerant number parser);
 *     otherwise falls back to localeCompare.
 *   - Cells can opt-in to a specific sort value via data-sort-value on
 *     the <td> — e.g. for cells containing non-text children like a
 *     sparkline canvas. Without it, textContent is used.
 *
 * Accessibility: updates aria-sort on the active column; other columns
 * get aria-sort="none".
 */
export default class extends Controller {
  connect() {
    this._headers = Array.from(this.element.querySelectorAll("th[data-sortable-table-column]"))
    this._currentColumn = null
    this._ascending = true

    this._headers.forEach((th, index) => {
      th.style.cursor = "pointer"
      th.setAttribute("aria-sort", "none")
      // Cache the column index so we can find the matching <td> per row.
      th.dataset.sortableTableIndex = String(index)
      // LRPO-5360 — a VISIBLE sort-direction icon on every sortable header, so
      // the sort affordance + the active column/direction are shown, not only
      // announced via aria-sort.
      this._ensureSortIcon(th)

      const handler = () => this._sortByHeader(th)
      th.addEventListener("click", handler)
      // Keep a reference so disconnect can remove listeners if the table
      // is swapped out by Turbo.
      th._sortableTableHandler = handler
    })

    // LRPO-5360 — reflect the server's default sort so the initial paint shows
    // WHICH column is sorted and WHICH way (rows arrive server-sorted; a header
    // marked data-sortable-table-default="asc|desc" flags it). Without a marker
    // every column just shows the neutral unsorted affordance.
    const defaultTh = this._headers.find(th => th.dataset.sortableTableDefault)
    if (defaultTh) {
      this._currentColumn = defaultTh.dataset.sortableTableColumn
      this._ascending = defaultTh.dataset.sortableTableDefault !== "desc"
      this._updateSortIndicators(defaultTh, this._ascending)
    } else {
      this._headers.forEach(th => this._setSortIcon(th, "none"))
    }
  }

  disconnect() {
    if (!this._headers) return
    this._headers.forEach(th => {
      if (th._sortableTableHandler) {
        th.removeEventListener("click", th._sortableTableHandler)
        delete th._sortableTableHandler
      }
    })
  }

  _sortByHeader(th) {
    const column = th.dataset.sortableTableColumn
    const index = Number(th.dataset.sortableTableIndex)
    if (!column || !Number.isFinite(index)) return

    const ascending = this._currentColumn === column ? !this._ascending : true
    const tbody = this.element.querySelector("tbody")
    if (!tbody) return

    const rows = Array.from(tbody.querySelectorAll("tr"))
    // Filter to rows that actually have the target cell — skip "drill-down"
    // turbo-frame placeholder rows from _rep_stats.html.erb.
    const sortable = rows.filter(r => r.children[index])

    sortable.sort((a, b) => {
      const aValue = this._cellSortValue(a.children[index])
      const bValue = this._cellSortValue(b.children[index])
      const cmp = this._compare(aValue, bValue)
      return ascending ? cmp : -cmp
    })

    sortable.forEach(row => tbody.appendChild(row))

    this._currentColumn = column
    this._ascending = ascending
    this._updateSortIndicators(th, ascending)
  }

  _cellSortValue(cell) {
    if (!cell) return ""
    const explicit = cell.dataset?.sortValue
    if (explicit !== undefined) return explicit
    return (cell.textContent || "").trim()
  }

  _compare(a, b) {
    const aNum = this._toNumber(a)
    const bNum = this._toNumber(b)
    if (aNum !== null && bNum !== null) return aNum - bNum
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" })
  }

  _toNumber(value) {
    if (value === "" || value === null || value === undefined) return null
    // Strip currency symbols, commas, percent signs — leave the numeric core.
    const cleaned = String(value).replace(/[,$%\s]/g, "")
    if (!/^-?\d*\.?\d+$/.test(cleaned)) return null
    const n = Number(cleaned)
    return Number.isFinite(n) ? n : null
  }

  // LRPO-5360 — update BOTH the a11y aria-sort AND the visible icon: the active
  // column shows a filled up/down caret, every other sortable column shows the
  // neutral "unsorted" glyph.
  _updateSortIndicators(activeHeader, ascending) {
    this._headers.forEach(th => {
      const active = th === activeHeader
      th.setAttribute("aria-sort", active ? (ascending ? "ascending" : "descending") : "none")
      this._setSortIcon(th, active ? (ascending ? "asc" : "desc") : "none")
    })
  }

  // Append a sort icon to a header once (idempotent across Turbo re-renders).
  _ensureSortIcon(th) {
    let icon = th.querySelector("[data-sortable-table-icon]")
    if (!icon) {
      icon = document.createElement("i")
      icon.setAttribute("data-sortable-table-icon", "")
      icon.setAttribute("aria-hidden", "true")
      icon.className = "sortable-table__sort-icon bi bi-arrow-down-up"
      th.appendChild(document.createTextNode(" "))
      th.appendChild(icon)
    }
    th._sortIcon = icon
  }

  // Swap the icon glyph + toggle the active-column class. state: 'asc'|'desc'|'none'.
  _setSortIcon(th, state) {
    const icon = th._sortIcon || th.querySelector("[data-sortable-table-icon]")
    if (!icon) return
    const glyph = state === "asc" ? "bi-caret-up-fill"
                : state === "desc" ? "bi-caret-down-fill"
                : "bi-arrow-down-up"
    icon.className = `sortable-table__sort-icon bi ${glyph}`
    th.classList.toggle("sortable-table__th--active", state === "asc" || state === "desc")
  }
}
